import type { SwitchProps } from '@mui/material'
import { FormControl, FormControlLabel, FormHelperText, Switch } from '@mui/material'
import { forwardRef } from 'react'
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'

export type SwitchControllerProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  SwitchProps,
  'name'
> & {
  name: TName
  label?: string
  // It enables showing field validation errors. Its default value is true
  showError?: boolean
  controllerProps?: Omit<ControllerProps<TFieldValues, TName>, 'render' | 'name'>
}

function SwitchField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
  props: Readonly<Omit<SwitchControllerProps<TFieldValues, TName>, 'controllerProps'>>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  if (!props.label) {
    return <Switch checked={Boolean(props.value)} {...props} ref={ref} />
  }

  return (
    <FormControlLabel ref={ref} control={<Switch checked={Boolean(props.value)} {...props} />} label={props.label} />
  )
}

const SwitchFieldWithRef = forwardRef(SwitchField)

function SwitchController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ label, name, showError = true, controllerProps, ...fieldProps }: SwitchControllerProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>()

  return (
    <Controller
      control={control}
      name={name}
      {...controllerProps}
      render={({ field, fieldState: { error, invalid } }) =>
        showError ? (
          <FormControl error={invalid}>
            <SwitchFieldWithRef {...field} checked={field.value ?? false} {...fieldProps} label={label} />

            {invalid && showError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        ) : (
          <FormControlLabel
            control={<Switch {...field} checked={field.value} name={name} {...fieldProps} />}
            label={label}
          />
        )
      }
    />
  )
}

export default SwitchController
